import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

export default function Filteredcontent() {
  Alpine.data("filteredcontent", (config = {}) => {
    const cfg = {
      endpoint: "/index.php?action=graphql/api",
      section: "grants",
      pageSize: 10,
      defaultSort: "latest",
      gqlFields: "id title url slug postDate",
      typeFilterField: "",
      ...config,
    };

    const ORDER_BY = {
      latest: "postDate DESC",
      oldest: "postDate ASC",
      alphabetical_asc: "title ASC",
      alphabetical_desc: "title DESC",
      amount_desc: "amount DESC",
      amount_asc: "amount ASC",
    };

    const defaultFilters = () => ({
      sort: cfg.defaultSort,
      search: "",
      years: [],
      partner: "",
      insightsType: [],
      focusArea: [],
      partnerEntryId: "",
    });

    // Alpine proxies can't be structuredCloned — copy each field explicitly
    const cloneFilters = (f) => ({
      sort: f.sort,
      search: f.search,
      years: [...f.years],
      partner: f.partner,
      insightsType: [...f.insightsType],
      focusArea: [...f.focusArea],
      partnerEntryId: f.partnerEntryId,
    });

    // Built once per component instance — only variables change between requests
    const GQL_QUERY = `
      query FilteredContent(
        $section: [String]
        $limit: Int
        $offset: Int
        $orderBy: String
        $search: String
        $datestart: [QueryArgument]
        $partner: [QueryArgument]
        ${cfg.typeFilterField ? "$insightsType: [QueryArgument]" : ""}
        $focusArea: [QueryArgument]
        $relatedToEntries: [EntryRelationCriteriaInput]
      ) {
        entries(
          section: $section
          limit: $limit
          offset: $offset
          orderBy: $orderBy
          search: $search
          datestart: $datestart
          partner: $partner
          ${cfg.typeFilterField ? `${cfg.typeFilterField}: $insightsType` : ""}
          focusArea: $focusArea
          relatedToEntries: $relatedToEntries
        ) {
          ${cfg.gqlFields}
        }
        entryCount(
          section: $section
          search: $search
          datestart: $datestart
          partner: $partner
          ${cfg.typeFilterField ? `${cfg.typeFilterField}: $insightsType` : ""}
          focusArea: $focusArea
          relatedToEntries: $relatedToEntries
        )
      }
    `;

    return {
      // ─── State ─────────────────────────────────────────────────────
      filterOpen: false,
      loading: false,
      items: [],
      total: 0,
      offset: 0,
      filters: defaultFilters(),
      pending: defaultFilters(),

      // ─── Computed ──────────────────────────────────────────────────
      get hasMore() {
        return this.items.length < this.total;
      },

      get activeFilterCount() {
        let n = 0;
        if (this.filters.sort !== cfg.defaultSort) n++;
        if (this.filters.search.trim()) n++;
        n += this.filters.years.length;
        if (this.filters.partner) n++;
        n += this.filters.insightsType.length;
        n += this.filters.focusArea.length;
        if (this.filters.partnerEntryId) n++;
        return n;
      },

      // ─── Lifecycle ─────────────────────────────────────────────────
      init() {
        this._readUrl();
        this._fetch();
        if (this.activeFilterCount > 0) {
          this.$nextTick(() => this._scrollToTop());
        }
        this.$el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" && this.filterOpen) {
            e.preventDefault(); // stops synthetic button click from reopening the overlay
            this.applyFilters();
          }
        });
      },

      // ─── Overlay ───────────────────────────────────────────────────
      openFilter() {
        this.pending = cloneFilters(this.filters);
        this.filterOpen = true;
      },

      closeFilter() {
        this.filterOpen = false;
      },

      // ─── Filter actions ────────────────────────────────────────────
      applyFilters() {
        this.filters = cloneFilters(this.pending);
        this.offset = 0;
        this.items = [];
        this._writeUrl();
        this.filterOpen = false;
        this._fetch();
        this._scrollToTop();
      },

      resetFilters() {
        this.filters = defaultFilters();
        this.pending = defaultFilters();
        this.offset = 0;
        this.items = [];
        this._writeUrl();
        this.filterOpen = false;
        this._fetch();
        this._scrollToTop();
      },

      // ─── Pagination ────────────────────────────────────────────────
      loadMore() {
        this.offset = this.items.length;
        this._fetch(true);
      },

      // ─── Formatters ────────────────────────────────────────────────
      localizedUrl(url) {
        const lang = typeof Weglot !== "undefined" && Weglot.getCurrentLang();
        if (!lang || lang === "en") return url;
        const u = new URL(url);
        return u.origin + "/" + lang + u.pathname + u.search + u.hash;
      },

      formatAmount(val) {
        if (!val) return "N/A";
        const millions = Number(val) / 1_000_000;
        const formatted =
          millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(2);
        return `€ ${formatted}M`;
      },

      // ─── URL sync ──────────────────────────────────────────────────
      _readUrl() {
        const p = new URLSearchParams(window.location.search);
        if (p.get("sort")) this.filters.sort = p.get("sort");
        if (p.get("search")) this.filters.search = p.get("search");
        if (p.get("years"))
          this.filters.years = p.get("years").split(",").filter(Boolean);
        if (p.get("partner")) this.filters.partner = p.get("partner");
        if (p.get("insightstype"))
          this.filters.insightsType = p
            .get("insightstype")
            .split(",")
            .filter(Boolean);
        if (p.get("focusarea"))
          this.filters.focusArea = p
            .get("focusarea")
            .split(",")
            .filter(Boolean);
        if (p.get("partnerid"))
          this.filters.partnerEntryId = p.get("partnerid");
        this.pending = cloneFilters(this.filters);
      },

      _writeUrl() {
        const p = new URLSearchParams();
        if (this.filters.sort !== cfg.defaultSort)
          p.set("sort", this.filters.sort);
        if (this.filters.search.trim())
          p.set("search", this.filters.search.trim());
        if (this.filters.years.length)
          p.set("years", this.filters.years.join(","));
        if (this.filters.partner) p.set("partner", this.filters.partner);
        if (this.filters.insightsType.length)
          p.set("insightstype", this.filters.insightsType.join(","));
        if (this.filters.focusArea.length)
          p.set("focusarea", this.filters.focusArea.join(","));
        if (this.filters.partnerEntryId)
          p.set("partnerid", this.filters.partnerEntryId);
        const qs = p.toString();
        window.history.replaceState(
          {},
          "",
          window.location.pathname + (qs ? "?" + qs : ""),
        );
      },

      // ─── Scroll ────────────────────────────────────────────────────
      _scrollToTop() {
        gsap.to(window, {
          duration: 0.8,
          scrollTo: this.$root,
          ease: "power2.inOut",
        });
      },

      // ─── Fetch ─────────────────────────────────────────────────────
      async _fetch(append = false) {
        if (this.loading) return;
        this.loading = true;

        const f = this.filters;

        const variables = {
          section: [cfg.section],
          limit: cfg.pageSize,
          offset: this.offset,
          orderBy: ORDER_BY[f.sort] ?? "postDate DESC",
        };

        if (f.search.trim()) variables.search = f.search.trim();

        if (f.years.length) {
          const sorted = f.years.map(Number).sort((a, b) => a - b);
          variables.datestart = [
            "and",
            `>= ${sorted[0]}-01-01`,
            `< ${sorted[sorted.length - 1] + 1}-01-01`,
          ];
        }

        if (f.partner) variables.partner = [f.partner];

        if (f.insightsType.length)
          variables.insightsType = f.insightsType.map(Number);

        if (f.focusArea.length) variables.focusArea = f.focusArea.map(Number);

        if (f.partnerEntryId)
          variables.relatedToEntries = [
            { section: ["partners"], id: [Number(f.partnerEntryId)] },
          ];

        try {
          const res = await fetch(cfg.endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ query: GQL_QUERY, variables }),
          });
          const json = await res.json();
          if (json.errors)
            console.error("[filteredcontent] GraphQL errors:", json.errors);
          const entries = json.data?.entries ?? [];
          this.total = json.data?.entryCount ?? 0;
          this.items = append ? [...this.items, ...entries] : entries;
        } catch (err) {
          console.error("[filteredcontent] Fetch error:", err);
        } finally {
          this.loading = false;
        }
      },
    };
  });
}
